import axiosInstance from '@/app/api/axiosInstance';

export interface CustomerItem {
    id: number;
    name: string;
    phone?: string | null;
    billing_address?: string | null;
    site_id?: number | null;
}

export interface CustomerListResponse {
    items: CustomerItem[];
    total: number;
}

export interface CreateReceiptPayload {
    party_id: number;
    amount: number;
    transaction_date?: string;
    reference_number?: string;
    narration?: string;
    /** Send the customer a WhatsApp confirmation of the cash received (backend
     *  defaults to true; skipped server-side if the party has no valid mobile). */
    send_whatsapp_confirmation?: boolean;
}

export interface ReceiptVoucher {
    id: number;
    voucher_number: string;
    total_amount?: number;
    transaction_date?: string;
}

export type CollectionStatus =
    | 'pending'
    | 'approved'
    | 'withdrawn'
    | 'rejected'
    | 'cancelled';

/**
 * Result of recording a collection — the same shape whichever mode the office runs.
 *
 * In `verified` mode the collection waits for the office to check it against the
 * cash, so `status` is 'pending' and `voucher` is null. In `direct` mode it posts
 * on the spot and `voucher` is populated. Handle both and the app keeps working
 * if the office ever changes the setting.
 */
export interface RecordReceiptResult {
    collection_id: number | null;
    status: CollectionStatus;
    mode: 'verified' | 'direct';
    party_id: number;
    party_name?: string | null;
    amount: number;
    collection_date: string;
    voucher: ReceiptVoucher | null;
    message?: string | null;
}

export interface MyCollection {
    id: number;
    party_id: number;
    party_name: string | null;
    amount: number;
    declared_amount: number;
    was_amended: boolean;
    collection_date: string;
    reference_number: string | null;
    narration: string | null;
    status: CollectionStatus;
    voucher_number: string | null;
    rejection_reason: string | null;
    created_at: string | null;
}

/**
 * Search the customers a field agent may record receipts for. Results are
 * scoped server-side to the agent's accessible sites.
 */
export const fetchCustomers = async (
    siteId: number,
    search = '',
    limit = 20,
): Promise<CustomerListResponse> => {
    const response = await axiosInstance.get<CustomerListResponse>('/field-agent/customers', {
        params: { search: search || undefined, limit, requested_site_id: siteId },
    });
    return response.data;
};

/**
 * Record cash collected on delivery. The receiving account is resolved
 * server-side (the agent's own cash account) and the full amount becomes party
 * unallocated credit — no invoice allocation.
 *
 * Normally the office verifies the collection against the cash before it is
 * posted, so the result comes back 'pending'. Check `status` rather than
 * assuming a voucher is present.
 */
export const createReceipt = async (
    siteId: number,
    payload: CreateReceiptPayload,
): Promise<RecordReceiptResult> => {
    const response = await axiosInstance.post<RecordReceiptResult>('/field-agent/receipts', payload, {
        params: { requested_site_id: siteId },
    });
    return response.data;
};

/** Everything this agent has recorded, newest first. */
export const fetchMyCollections = async (
    status?: CollectionStatus,
    limit = 50,
): Promise<{ items: MyCollection[]; total: number }> => {
    const response = await axiosInstance.get<{ items: MyCollection[]; total: number }>(
        '/field-agent/collections',
        { params: { status: status || undefined, limit } },
    );
    return response.data;
};

/** Fix a mistake before the office reviews it. Pending collections only. */
export const updateMyCollection = async (
    collectionId: number,
    data: { party_id?: number; amount?: number; narration?: string },
): Promise<MyCollection> => {
    const response = await axiosInstance.patch<MyCollection>(
        `/field-agent/collections/${collectionId}`,
        data,
    );
    return response.data;
};

/** Remove a collection recorded in error, before the office reviews it. */
export const withdrawMyCollection = async (collectionId: number): Promise<MyCollection> => {
    const response = await axiosInstance.delete<MyCollection>(
        `/field-agent/collections/${collectionId}`,
    );
    return response.data;
};
